import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Loader2, Eye, AlertTriangle, FileWarning, RefreshCw, Send, Pencil, RotateCcw, Save, Code, History,
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

interface TemplatePreview {
  templateName: string;
  displayName: string;
  subject: string;
  html: string;
  status: "ready" | "preview_data_required" | "render_failed";
  errorMessage?: string;
}

interface TemplateOverride {
  id: string;
  template_name: string;
  subject_override: string | null;
  html_override: string;
  is_active: boolean;
}

interface TemplateVersion {
  id: string;
  template_name: string;
  subject_override: string | null;
  html_override: string;
  saved_by: string | null;
  created_at: string;
}

const EmailTemplatePreview = () => {
  const [templates, setTemplates] = useState<TemplatePreview[]>([]);
  const [overrides, setOverrides] = useState<Record<string, TemplateOverride>>({});
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState<TemplatePreview | null>(null);
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState<string | null>(null);

  // Editor state
  const [editingTemplate, setEditingTemplate] = useState<TemplatePreview | null>(null);
  const [editorHtml, setEditorHtml] = useState("");
  const [editorSubject, setEditorSubject] = useState("");
  const [editorActive, setEditorActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editorTab, setEditorTab] = useState<string>("code");

  // Version history state
  const [versions, setVersions] = useState<TemplateVersion[]>([]);
  const [loadingVersions, setLoadingVersions] = useState(false);

  // Role variant state for role-approved-welcome template
  const [roleVariant, setRoleVariant] = useState<string>("provider");
  const [variantLoading, setVariantLoading] = useState(false);

  const ROLE_VARIANT_TEMPLATE = "role-approved-welcome";
  const roleVariantOptions = [
    { value: "provider", label: "Provider" },
    { value: "salesperson", label: "Salesperson" },
    { value: "pharmacy", label: "Pharmacy" },
  ];

  const fetchRoleVariant = async (role: string) => {
    setVariantLoading(true);
    try {
      const recipientName = role === "provider" ? "Dr. Jane Smith" : "Jane Smith";
      const { data, error } = await supabase.functions.invoke("admin-preview-templates", {
        body: {
          template: ROLE_VARIANT_TEMPLATE,
          preview_data: { approvedRole: role, recipientName },
        },
      });
      if (error) throw error;
      const tpl = data?.templates?.[0];
      if (tpl && selectedTemplate?.templateName === ROLE_VARIANT_TEMPLATE) {
        setSelectedTemplate((prev) => prev ? { ...prev, html: tpl.html, subject: tpl.subject } : null);
      }
    } catch (err: any) {
      toast.error("Failed to load variant", { description: err.message });
    } finally {
      setVariantLoading(false);
    }
  };

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const [templatesRes, overridesRes] = await Promise.all([
        supabase.functions.invoke("admin-preview-templates"),
        supabase.from("email_template_overrides").select("*"),
      ]);
      if (templatesRes.error) throw templatesRes.error;
      const templateList = Array.isArray(templatesRes.data?.templates) ? templatesRes.data.templates : [];
      setTemplates(templateList as TemplatePreview[]);

      const oMap: Record<string, TemplateOverride> = {};
      (overridesRes.data || []).forEach((o: any) => { oMap[o.template_name] = o; });
      setOverrides(oMap);
    } catch (err: any) {
      toast.error("Failed to load templates", { description: err.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTemplates(); }, []);

  const fetchVersions = async (templateName: string) => {
    setLoadingVersions(true);
    try {
      const { data, error } = await supabase
        .from("email_template_versions")
        .select("*")
        .eq("template_name", templateName)
        .order("created_at", { ascending: false })
        .limit(20);
      if (error) throw error;
      setVersions((data as TemplateVersion[]) || []);
    } catch (err: any) {
      toast.error("Failed to load version history", { description: err.message });
    } finally {
      setLoadingVersions(false);
    }
  };

  const openEditor = (t: TemplatePreview) => {
    const override = overrides[t.templateName];
    setEditingTemplate(t);
    setEditorHtml(override?.html_override || t.html);
    setEditorSubject(override?.subject_override || t.subject || "");
    setEditorActive(override?.is_active ?? true);
    setEditorTab("code");
    setVersions([]);
    fetchVersions(t.templateName);
  };

  const handleSave = async () => {
    if (!editingTemplate) return;
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();

      // Save current state as a version snapshot before overwriting
      await supabase.from("email_template_versions").insert({
        template_name: editingTemplate.templateName,
        subject_override: editorSubject || null,
        html_override: editorHtml,
        saved_by: user?.id || null,
      });

      const existing = overrides[editingTemplate.templateName];

      if (existing) {
        const { error } = await supabase
          .from("email_template_overrides")
          .update({
            html_override: editorHtml,
            subject_override: editorSubject || null,
            is_active: editorActive,
            updated_by: user?.id || null,
          })
          .eq("template_name", editingTemplate.templateName);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("email_template_overrides")
          .insert({
            template_name: editingTemplate.templateName,
            html_override: editorHtml,
            subject_override: editorSubject || null,
            is_active: editorActive,
            updated_by: user?.id || null,
          });
        if (error) throw error;
      }

      toast.success("Template override saved");
      await fetchTemplates();
      setEditingTemplate(null);
    } catch (err: any) {
      toast.error("Failed to save", { description: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefault = async () => {
    if (!editingTemplate) return;
    const existing = overrides[editingTemplate.templateName];
    if (!existing) {
      setEditorHtml(editingTemplate.html);
      setEditorSubject(editingTemplate.subject || "");
      toast.info("Reset to default template");
      return;
    }
    setSaving(true);
    try {
      const { error } = await supabase
        .from("email_template_overrides")
        .delete()
        .eq("template_name", editingTemplate.templateName);
      if (error) throw error;
      toast.success("Override removed — using default template");
      await fetchTemplates();
      setEditingTemplate(null);
    } catch (err: any) {
      toast.error("Failed to reset", { description: err.message });
    } finally {
      setSaving(false);
    }
  };

  const restoreVersion = (v: TemplateVersion) => {
    setEditorHtml(v.html_override);
    setEditorSubject(v.subject_override || "");
    setEditorTab("code");
    toast.info("Version restored into editor — click Save Override to apply");
  };

  const sendTestEmail = async (templateName: string) => {
    const email = testEmail.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }
    setSendingTest(templateName);
    try {
      const { data, error } = await supabase.functions.invoke("send-transactional-email", {
        body: {
          templateName,
          recipientEmail: email,
          idempotencyKey: `test-${templateName}-${Date.now()}`,
        },
      });
      if (error) throw error;
      if (data?.success) {
        toast.success("Test email queued", { description: `Sending "${templateName}" to ${email}` });
      } else {
        toast.warning("Email not sent", { description: data?.reason || "Unknown reason" });
      }
    } catch (err: any) {
      toast.error("Failed to send test email", { description: err.message });
    } finally {
      setSendingTest(null);
    }
  };

  const statusBadge = (status: TemplatePreview["status"]) => {
    switch (status) {
      case "ready":
        return <Badge className="bg-emerald-500/15 text-emerald-700 border-emerald-500/30">Ready</Badge>;
      case "preview_data_required":
        return <Badge className="bg-amber-500/15 text-amber-700 border-amber-500/30">No Preview Data</Badge>;
      case "render_failed":
        return <Badge variant="destructive">Render Failed</Badge>;
    }
  };

  const formatTemplateName = (name: string) =>
    name.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Preview and customize your email templates. Overrides take priority over default templates.
        </p>
        <Button variant="outline" size="sm" onClick={fetchTemplates} className="gap-1.5">
          <RefreshCw size={14} /> Refresh
        </Button>
      </div>

      <div className="flex items-center gap-2 max-w-md">
        <Input
          type="email"
          placeholder="Enter test recipient email…"
          value={testEmail}
          onChange={(e) => setTestEmail(e.target.value)}
          className="text-sm"
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {templates.map((t) => {
          const hasOverride = !!overrides[t.templateName];
          return (
            <Card key={t.templateName} className="border-border hover:shadow-md transition-shadow">
              <CardContent className="pt-5 pb-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="font-medium text-sm truncate">{t.displayName || formatTemplateName(t.templateName)}</h3>
                    {t.subject && (
                      <p className="text-xs text-muted-foreground truncate mt-0.5">
                        Subject: {t.subject}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    {hasOverride && (
                      <Badge variant="outline" className="text-xs border-primary/40 text-primary">
                        Customized
                      </Badge>
                    )}
                    {statusBadge(t.status)}
                  </div>
                </div>

                {t.status === "ready" ? (
                  <div
                    className="border rounded-md bg-white overflow-hidden h-40 relative group cursor-pointer"
                    onClick={() => setSelectedTemplate(t)}
                  >
                    <iframe
                      srcDoc={hasOverride && overrides[t.templateName].is_active ? overrides[t.templateName].html_override : t.html}
                      className="w-[200%] h-[200%] origin-top-left pointer-events-none"
                      style={{ transform: "scale(0.5)" }}
                      sandbox=""
                      title={`Preview of ${t.displayName}`}
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors flex items-center justify-center">
                      <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-background/90 text-foreground text-xs font-medium px-3 py-1.5 rounded-full shadow-sm flex items-center gap-1.5">
                        <Eye size={12} /> Click to preview
                      </span>
                    </div>
                  </div>
                ) : t.status === "preview_data_required" ? (
                  <div className="border rounded-md bg-muted/30 h-40 flex flex-col items-center justify-center gap-2 text-muted-foreground">
                    <FileWarning size={24} />
                    <p className="text-xs">No preview data configured</p>
                  </div>
                ) : (
                  <div className="border rounded-md bg-destructive/5 h-40 flex flex-col items-center justify-center gap-2 text-destructive">
                    <AlertTriangle size={24} />
                    <p className="text-xs text-center px-4">{t.errorMessage || "Render failed"}</p>
                  </div>
                )}

                <div className="flex gap-2">
                  {t.status === "ready" && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 gap-1.5"
                        onClick={() => setSelectedTemplate(t)}
                      >
                        <Eye size={14} /> Preview
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 gap-1.5"
                        onClick={() => openEditor(t)}
                      >
                        <Pencil size={14} /> Edit
                      </Button>
                      <Button
                        variant="default"
                        size="sm"
                        className="flex-1 gap-1.5"
                        disabled={!testEmail.trim() || sendingTest === t.templateName}
                        onClick={() => sendTestEmail(t.templateName)}
                      >
                        {sendingTest === t.templateName ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <Send size={14} />
                        )}
                        Test
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}

        {templates.length === 0 && (
          <div className="col-span-full text-center py-12 text-muted-foreground">
            <p className="text-sm">No email templates found.</p>
          </div>
        )}
      </div>

      {/* Preview Dialog */}
      <Dialog open={!!selectedTemplate && !editingTemplate} onOpenChange={() => setSelectedTemplate(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedTemplate?.displayName || formatTemplateName(selectedTemplate?.templateName || "")}
              {selectedTemplate && statusBadge(selectedTemplate.status)}
            </DialogTitle>
            {selectedTemplate?.subject && (
              <p className="text-sm text-muted-foreground">
                Subject: {selectedTemplate.subject}
              </p>
            )}
          </DialogHeader>

          {/* Role variant selector for role-approved-welcome */}
          {selectedTemplate?.templateName === ROLE_VARIANT_TEMPLATE && (
            <div className="flex items-center gap-3 pb-1">
              <Label className="text-sm shrink-0">Preview role:</Label>
              <Select
                value={roleVariant}
                onValueChange={(val) => {
                  setRoleVariant(val);
                  fetchRoleVariant(val);
                }}
              >
                <SelectTrigger className="w-44">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roleVariantOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {variantLoading && <Loader2 size={14} className="animate-spin text-muted-foreground" />}
            </div>
          )}

          <div className="flex-1 overflow-auto border rounded-md bg-white min-h-[400px]">
            {selectedTemplate?.html && (
              <iframe
                srcDoc={
                  selectedTemplate && overrides[selectedTemplate.templateName]?.is_active
                    ? overrides[selectedTemplate.templateName].html_override
                    : selectedTemplate.html
                }
                className="w-full h-full min-h-[500px]"
                sandbox=""
                title="Email template preview"
              />
            )}
          </div>
          {selectedTemplate?.status === "ready" && (
            <div className="flex items-center gap-2 pt-2">
              <Input
                type="email"
                placeholder="Recipient email for test…"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                className="text-sm"
              />
              <Button
                className="gap-1.5 shrink-0"
                disabled={!testEmail.trim() || sendingTest === selectedTemplate.templateName}
                onClick={() => sendTestEmail(selectedTemplate.templateName)}
              >
                {sendingTest === selectedTemplate.templateName ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Send size={14} />
                )}
                Send Test Email
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Editor Dialog */}
      <Dialog open={!!editingTemplate} onOpenChange={() => setEditingTemplate(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Code size={18} />
              Edit: {editingTemplate?.displayName || formatTemplateName(editingTemplate?.templateName || "")}
              {overrides[editingTemplate?.templateName || ""] && (
                <Badge variant="outline" className="text-xs border-primary/40 text-primary">Customized</Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 flex-1 overflow-hidden flex flex-col">
            {/* Subject line */}
            <div className="flex items-center gap-3">
              <Label className="shrink-0 text-sm w-16">Subject</Label>
              <Input
                value={editorSubject}
                onChange={(e) => setEditorSubject(e.target.value)}
                placeholder="Email subject line…"
                className="text-sm"
              />
              <div className="flex items-center gap-2 shrink-0">
                <Switch checked={editorActive} onCheckedChange={setEditorActive} id="override-active" />
                <Label htmlFor="override-active" className="text-xs text-muted-foreground cursor-pointer">Active</Label>
              </div>
            </div>

            {/* Code / Preview / Split / History tabs */}
            <Tabs value={editorTab} onValueChange={setEditorTab} className="flex-1 flex flex-col overflow-hidden">
              <TabsList className="w-fit">
                <TabsTrigger value="code" className="gap-1.5 text-xs">
                  <Code size={12} /> HTML Code
                </TabsTrigger>
                <TabsTrigger value="preview" className="gap-1.5 text-xs">
                  <Eye size={12} /> Live Preview
                </TabsTrigger>
                <TabsTrigger value="split" className="gap-1.5 text-xs">
                  Split View
                </TabsTrigger>
                <TabsTrigger value="history" className="gap-1.5 text-xs">
                  <History size={12} /> History
                  {versions.length > 0 && (
                    <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-[10px]">
                      {versions.length}
                    </Badge>
                  )}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="code" className="flex-1 overflow-hidden mt-2">
                <Textarea
                  value={editorHtml}
                  onChange={(e) => setEditorHtml(e.target.value)}
                  className="h-full min-h-[400px] font-mono text-xs resize-none"
                  placeholder="Paste or edit your email HTML here…"
                />
              </TabsContent>

              <TabsContent value="preview" className="flex-1 overflow-hidden mt-2">
                <div className="border rounded-md bg-white h-full min-h-[400px]">
                  <iframe
                    srcDoc={editorHtml}
                    className="w-full h-full min-h-[400px]"
                    sandbox=""
                    title="Template editor preview"
                  />
                </div>
              </TabsContent>

              <TabsContent value="split" className="flex-1 overflow-hidden mt-2">
                <div className="grid grid-cols-2 gap-3 h-full min-h-[400px]">
                  <Textarea
                    value={editorHtml}
                    onChange={(e) => setEditorHtml(e.target.value)}
                    className="h-full font-mono text-xs resize-none"
                    placeholder="Paste or edit your email HTML here…"
                  />
                  <div className="border rounded-md bg-white overflow-auto">
                    <iframe
                      srcDoc={editorHtml}
                      className="w-full h-full min-h-[400px]"
                      sandbox=""
                      title="Template editor preview"
                    />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="history" className="flex-1 overflow-hidden mt-2">
                {loadingVersions ? (
                  <div className="flex items-center justify-center py-16">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                ) : versions.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-2">
                    <History size={32} className="opacity-40" />
                    <p className="text-sm">No version history yet</p>
                    <p className="text-xs">Versions are saved each time you click Save Override</p>
                  </div>
                ) : (
                  <ScrollArea className="h-full min-h-[400px]">
                    <div className="space-y-3 pr-4">
                      {versions.map((v) => (
                        <div
                          key={v.id}
                          className="border rounded-lg p-3 flex gap-3 hover:bg-muted/30 transition-colors"
                        >
                          {/* Mini preview */}
                          <div className="w-28 h-20 border rounded bg-white overflow-hidden shrink-0">
                            <iframe
                              srcDoc={v.html_override}
                              className="w-[200%] h-[200%] origin-top-left pointer-events-none"
                              style={{ transform: "scale(0.35)" }}
                              sandbox=""
                              title="Version preview"
                            />
                          </div>
                          <div className="flex-1 min-w-0 flex flex-col justify-between">
                            <div>
                              <p className="text-xs font-medium text-foreground">
                                {format(new Date(v.created_at), "MMM d, yyyy 'at' h:mm a")}
                              </p>
                              {v.subject_override && (
                                <p className="text-xs text-muted-foreground truncate mt-0.5">
                                  Subject: {v.subject_override}
                                </p>
                              )}
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              className="w-fit gap-1.5 mt-1"
                              onClick={() => restoreVersion(v)}
                            >
                              <RotateCcw size={12} /> Restore
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </TabsContent>
            </Tabs>
          </div>

          <DialogFooter className="flex items-center justify-between sm:justify-between pt-2">
            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5 text-destructive hover:text-destructive"
              onClick={handleResetToDefault}
              disabled={saving}
            >
              <RotateCcw size={14} /> Reset to Default
            </Button>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setEditingTemplate(null)}>
                Cancel
              </Button>
              <Button size="sm" className="gap-1.5" onClick={handleSave} disabled={saving}>
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Save Override
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailTemplatePreview;
